import React from 'react';
import Skeleton from 'react-loading-skeleton';
import ServerError from '../../../../../../../components/ServerError';
import { useApprovalRequestShiftChangeRequestQuery } from '../../../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import FormattedDateTime from '../../../../../../../components/datetime/FormattedDateTime';
import { ShiftDisplay } from '../../../../SchedulerShifts/SchedulerShiftsCalendar/ShiftDisplay';

type Props = {
  shiftChangeRequestId: string;
};

export const ShiftDetails: React.FC<React.PropsWithChildren<Props>> = ({
  shiftChangeRequestId,
}) => {
  const { data, loading, error } = useApprovalRequestShiftChangeRequestQuery({
    variables: {
      shiftChangeRequestId,
    },
  });

  const t = useNamespacedTranslation(
    'approvalRequest.shiftChangeRequestDetailsSidebar',
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ServerError />;
  }

  const shiftChangeRequest = data?.shiftChangeRequest;

  if (!shiftChangeRequest) {
    return <ServerError />;
  }

  return (
    <div className="d-flex flex-column h-100">
      {
        <Section
          label={t('shiftDetails')}
          icon={<FlairIcon icon="calendar" size="sm" />}
          value={
            <div>
              <ShiftDisplay
                title={shiftChangeRequest.shift.flair__Title__c || ''}
                startDate={shiftChangeRequest.shift.flair__Start_Datetime__c}
                endDate={shiftChangeRequest.shift.flair__End_Datetime__c}
              />
            </div>
          }
        />
      }
      {shiftChangeRequest.proposedResourceEmployee && (
        <Section
          label={t('proposedEmployee')}
          value={shiftChangeRequest.proposedResourceEmployee.Name}
          icon={<FlairIcon icon="people-outline" size="sm" />}
        />
      )}
      {shiftChangeRequest.flair__Colleague_Responded_At__c && (
        <Section
          label={t('colleagueRespondedAt')}
          value={
            <FormattedDateTime
              dateTime={shiftChangeRequest.flair__Colleague_Responded_At__c}
              format="long"
            />
          }
          icon={<FlairIcon icon="time-outline" size="sm" />}
        />
      )}
      {shiftChangeRequest.flair__Change_Reason__c && (
        <Section
          label={t('changeReason')}
          value={shiftChangeRequest.flair__Change_Reason__c}
          icon={<FlairIcon icon="information-circle-outline" size="sm" />}
        />
      )}
    </div>
  );
};

export const Loading: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <div>
      <Skeleton height={80} />
      <Skeleton height={80} />
      <Skeleton height={80} />
    </div>
  );
};

type SectionProps = {
  label?: string;
  value: React.ReactNode;
  icon: React.ReactNode;
};

const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({
  label,
  value,
  icon,
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className="d-flex align-items-start mb-3 gap-3">
      {!!icon && <div className="text-muted flex-shrink-0 mt-1">{icon}</div>}
      <div className="flex-grow-1 d-flex flex-column gap-1">
        {label && <div className="fw-semibold mb-1">{label}</div>}
        <div style={{ lineHeight: 1.4 }}>{value}</div>
      </div>
    </div>
  );
};
